import {lazy} from 'react';

import {
  createRoute,
  redirectRoute,
  layoutComponent,
  requireSessionBeforeLoad,
  withRouter,
  type AnyRoute
} from 'client/routeHelpers';

const CreateAccounts = lazy(() => import('client/components/CreateAccounts/CreateAccounts.react'));
const SchoolAdmin = lazy(() => import('client/SchoolAdministration/SchoolAdministration'));
const SchoolList = lazy(() => import('client/SchoolAdministration/SchoolList/SchoolList.react'));
const School = lazy(() => import('client/SchoolAdministration/School/School'));
const SchoolAdminDashboard = lazy(
  () => import('client/SchoolAdministration/School/Dashboard/Dashboard.react')
);
const SchoolAdminManageStaff = lazy(
  () => import('client/SchoolAdministration/School/ManageStaff/ManageStaff')
);
const SchoolAccounts = lazy(
  () => import('client/SchoolAdministration/School/SchoolAccounts/SchoolAccounts.react')
);
const SchoolAdminUsageReports = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/UsageReports')
);
const SchoolAdminTeachersReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/TeachersReport.react')
);
const SchoolAdminStudentsReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/StudentsReport.react')
);
const SchoolAdminClassesReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/ClassesReport.react')
);
const SchoolAdminSubjectsReport = lazy(
  () => import('client/SchoolAdministration/School/UsageReports/Reports/SubjectsReport.react')
);

/**
 * `/school-admin` — school list at the index, and a `$schoolId` layout that
 * fans out into dashboard, manage-staff, usage-reports, and account tools.
 * 1:1 replacement of the RR3 route module (same paths, redirects, indexes).
 *
 * The session guard is what makes an emailed school link work signed out; `SchoolAdministration`'s
 * own signed-out branch renders the UNAUTHORIZED page, which offers no way in. That branch is still
 * the fallback for a session that dies while the page is open, and a signed-in non-admin keeps
 * getting the error page.
 */
export function makeSchoolAdminRoute(parentRoute: AnyRoute): AnyRoute {
  const schoolAdminRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: 'school-admin',
    component: layoutComponent(SchoolAdmin),
    beforeLoad: requireSessionBeforeLoad
  });

  const schoolAdminIndex = createRoute({
    getParentRoute: () => schoolAdminRoute,
    path: '/',
    component: withRouter(SchoolList)
  });

  // --- $schoolId layout ---
  const schoolIdRoute = createRoute({
    getParentRoute: () => schoolAdminRoute,
    path: '$schoolId',
    component: layoutComponent(School)
  });

  const schoolIdIndexRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: '/',
    to: '/school-admin/$schoolId/dashboard'
  });
  const dashboardRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'dashboard',
    component: withRouter(SchoolAdminDashboard)
  });
  const classesRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'classes',
    to: '/school-admin/$schoolId/usage-reports/classes'
  });
  const teachersRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'teachers',
    to: '/school-admin/$schoolId/usage-reports/teachers'
  });
  const licensesRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'licenses',
    to: '/school-admin/$schoolId/manage-staff'
  });

  // --- manage-staff layout ---
  const manageStaffRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'manage-staff',
    component: layoutComponent(SchoolAdminManageStaff)
  });
  const manageStaffIndexRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: '/',
    to: '/school-admin/$schoolId/manage-staff/review-needed'
  });
  // ManageStaff renders the active tab itself (it never renders its route
  // children), so the tab routes are componentless — they exist only so the
  // URL matches and ManageStaff can derive the active tab from `routes`.
  const reviewNeededRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'review-needed'
  });
  const allStaffRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'all-staff'
  });
  const invitesRoute = createRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'invites'
  });
  // Legacy manage-staff tab URLs (pre manage_staff_v2) → nearest new tab.
  const pendingApprovalRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'pending-approval',
    to: '/school-admin/$schoolId/manage-staff/review-needed'
  });
  const pendingInvitationsRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'pending-invitations',
    to: '/school-admin/$schoolId/manage-staff/invites'
  });
  const approvedRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'approved',
    to: '/school-admin/$schoolId/manage-staff/all-staff'
  });
  const rejectedRedirect = redirectRoute({
    getParentRoute: () => manageStaffRoute,
    path: 'rejected',
    to: '/school-admin/$schoolId/manage-staff/all-staff'
  });
  manageStaffRoute.addChildren([
    manageStaffIndexRedirect,
    reviewNeededRoute,
    allStaffRoute,
    invitesRoute,
    pendingApprovalRedirect,
    pendingInvitationsRedirect,
    approvedRedirect,
    rejectedRedirect
  ]);

  // Legacy update-teachers page (pre manage_staff_v2) → manage-staff.
  const updateTeachersRedirect = redirectRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'update-teachers',
    to: '/school-admin/$schoolId/manage-staff/review-needed'
  });

  // --- usage-reports layout ---
  const usageReportsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'usage-reports',
    component: layoutComponent(SchoolAdminUsageReports)
  });
  const usageReportsIndexRedirect = redirectRoute({
    getParentRoute: () => usageReportsRoute,
    path: '/',
    to: '/school-admin/$schoolId/usage-reports/teachers'
  });
  const usageTeachersRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'teachers',
    component: withRouter(SchoolAdminTeachersReport)
  });
  const usageStudentsRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'students',
    component: withRouter(SchoolAdminStudentsReport)
  });
  const usageClassesRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'classes',
    component: withRouter(SchoolAdminClassesReport)
  });
  const usageSubjectsRoute = createRoute({
    getParentRoute: () => usageReportsRoute,
    path: 'subjects',
    component: withRouter(SchoolAdminSubjectsReport)
  });
  usageReportsRoute.addChildren([
    usageReportsIndexRedirect,
    usageTeachersRoute,
    usageStudentsRoute,
    usageClassesRoute,
    usageSubjectsRoute
  ]);

  const schoolAccountsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'school-accounts',
    component: withRouter(SchoolAccounts)
  });
  const createAccountsRoute = createRoute({
    getParentRoute: () => schoolIdRoute,
    path: 'create-accounts',
    component: withRouter(CreateAccounts)
  });

  schoolIdRoute.addChildren([
    schoolIdIndexRedirect,
    dashboardRoute,
    classesRedirect,
    teachersRedirect,
    licensesRedirect,
    manageStaffRoute,
    updateTeachersRedirect,
    usageReportsRoute,
    schoolAccountsRoute,
    createAccountsRoute
  ]);

  return schoolAdminRoute.addChildren([schoolAdminIndex, schoolIdRoute]);
}
